import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchWorkChildren, fetchWorkSections, hasChildren, type Work } from './api';
import { canDropOn, isDraggable, type TreeNode } from './treeRules';

const LEVEL_COLORS = ['', '#EBEBEB', '#CDCDCD', '#A5A1A1', '#807D7D', '#5F5B5B', '#5F5050', '#473131', '#251E1E', '#1A1818'];

interface DragProps {
  dragging: TreeNode | null;
  onDragChange: (node: TreeNode | null) => void;
  /** `target === null` means "make a section". */
  onDropOn: (source: TreeNode, target: TreeNode | null) => void;
}

interface ActionProps {
  selectedId: number | null;
  onSelect: (node: TreeNode) => void;
  onDelete: (node: TreeNode) => void;
}

interface NodeProps extends DragProps, ActionProps {
  work: Work;
  level: number;
}

const WorkNode = ({ work, level, selectedId, onSelect, onDelete, dragging, onDragChange, onDropOn }: NodeProps) => {
  const [expanded, setExpanded] = useState(false);
  const [isOver, setIsOver] = useState(false);
  const node: TreeNode = { work, level };

  const children = useQuery({
    queryKey: ['works', 'children', work.id],
    queryFn: () => fetchWorkChildren(work.id),
    enabled: expanded,
  });

  const canDrop = dragging !== null && canDropOn(dragging, node);

  return (
    <>
      <div
        draggable={isDraggable(node)}
        onDragStart={() => onDragChange(node)}
        onDragEnd={() => onDragChange(null)}
        onDragOver={(e) => canDrop && e.preventDefault()}
        onDragEnter={() => canDrop && setIsOver(true)}
        onDragLeave={() => setIsOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsOver(false);
          if (dragging && canDrop) onDropOn(dragging, node);
        }}
        style={{
          padding: `4px 0 4px ${level}em`,
          borderLeft: level ? `solid ${LEVEL_COLORS[Math.min(level, 9)]}` : undefined,
          background: isOver ? '#deebff' : undefined,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <span style={{ width: 28, flexShrink: 0, cursor: 'pointer' }} onClick={() => setExpanded((prev) => !prev)}>
          {hasChildren(work) && <small className="label bg-blue">{expanded ? '-' : '+'}</small>}
        </span>
        <span
          style={{ cursor: 'pointer', flexGrow: 1, fontWeight: selectedId === work.id ? 700 : 400 }}
          onClick={() => onSelect(node)}
        >
          {work.name}
        </span>
        <span style={{ cursor: 'pointer', marginRight: 8 }} title="Удалить" onClick={() => onDelete(node)}>
          <i className="fa fa-trash-o text-red" />
        </span>
      </div>
      {expanded &&
        children.data?.map((child) => (
          <WorkNode
            key={child.id}
            work={child}
            level={level + 1}
            selectedId={selectedId}
            onSelect={onSelect}
            onDelete={onDelete}
            dragging={dragging}
            onDragChange={onDragChange}
            onDropOn={onDropOn}
          />
        ))}
    </>
  );
};

interface TreeProps extends DragProps, ActionProps {}

/** Lazy-loaded work types tree. Dragging a work onto another one moves it, the top zone makes it a section. */
export const WorkTree = ({ selectedId, onSelect, onDelete, dragging, onDragChange, onDropOn }: TreeProps) => {
  const [isOverRoot, setIsOverRoot] = useState(false);
  const sections = useQuery({ queryKey: ['works', 'sections'], queryFn: fetchWorkSections });

  const canMakeSection = dragging !== null && Boolean(dragging.work.parent_id);

  return (
    <div>
      {canMakeSection && (
        <div
          className="callout callout-info"
          style={{ background: isOverRoot ? '#deebff' : undefined, color: '#333' }}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={() => setIsOverRoot(true)}
          onDragLeave={() => setIsOverRoot(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsOverRoot(false);
            onDropOn(dragging, null);
          }}
        >
          Перетащите сюда, чтобы сделать корневым элементом
        </div>
      )}
      {sections.isLoading && <p>Загрузка…</p>}
      {sections.error && <div className="alert alert-danger">Ошибка загрузки услуг</div>}
      {sections.data?.map((work) => (
        <WorkNode
          key={work.id}
          work={work}
          level={0}
          selectedId={selectedId}
          onSelect={onSelect}
          onDelete={onDelete}
          dragging={dragging}
          onDragChange={onDragChange}
          onDropOn={onDropOn}
        />
      ))}
    </div>
  );
};
